import React from 'react';
import {
  Box,
  Typography,
  Container,
  Link,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getVersionInfo } from '../../config/version';
import {
  Security,
  Speed,
  CloudDone
} from '@mui/icons-material';

function Footer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentYear = new Date().getFullYear();
  const versionInfo = getVersionInfo();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        mt: 'auto' // Push footer to bottom
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          {/* Main Footer Content */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: 3,
              textAlign: { xs: 'center', md: 'left' }
            }}
          >
            {/* CCRS Info Section */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  mb: 1
                }}
              >
                CCRS Operations Center
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Fleet and operations management system for Craig County Rescue Squad
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                Secure, reliable, and designed for field operations
              </Typography>
            </Box>

            {/* System Features (Desktop only) */}
            {!isMobile && (
              <Box sx={{ flex: 1, maxWidth: 300 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}
                >
                  System Features
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Security sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="caption" color="text.secondary">
                      Google OAuth Authentication
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudDone sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="caption" color="text.secondary">
                      Real-time Firebase Sync
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Speed sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="caption" color="text.secondary">
                      Mobile-Optimized Interface
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Contact/Support Section */}
            <Box sx={{ flex: 1, maxWidth: { xs: '100%', md: 250 } }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}
              >
                Support & Contact
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Technical Issues:</strong>
                </Typography>
                <Link
                  href="mailto:officers@craigrescue.com"
                  color="primary"
                  variant="caption"
                  sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  officers@craigrescue.com
                </Link>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Access Requests:</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Contact your administrator for Google Group access
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Bottom Footer */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              textAlign: { xs: 'center', sm: 'left' }
            }}
          >
            {/* Copyright */}
            <Typography variant="caption" color="text.secondary">
              © {currentYear} Craig County Rescue Squad. All rights reserved.
            </Typography>

            {/* Version & Tech Info */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                gap: { xs: 1, sm: 3 }
              }}
            >
              <Typography variant="caption" color="text.secondary">
                React v{React.version}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Firebase Integration
              </Typography>
              <Link
                component={RouterLink}
                to="/release-notes"
                color="primary.main"
                variant="caption"
                sx={{ 
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': { 
                    textDecoration: 'underline' 
                  }
                }}
              >
                {versionInfo.fullVersion} - Release Notes
              </Link>
            </Box>
          </Box>

          {/* Mobile Features List */}
          {isMobile && (
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 3,
                  flexWrap: 'wrap'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Security sx={{ fontSize: 14, color: 'primary.main' }} />
                  <Typography variant="caption" color="text.secondary">
                    Secure
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CloudDone sx={{ fontSize: 14, color: 'primary.main' }} />
                  <Typography variant="caption" color="text.secondary">
                    Cloud Sync
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Speed sx={{ fontSize: 14, color: 'primary.main' }} />
                  <Typography variant="caption" color="text.secondary">
                    Fast
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;